import React from 'react';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Deadline pill for reviewer / approver work. The backend stores due dates as
// the UTC instant that ends the day in IST, so everything here is compared in
// IST to match what the reminder emails say.

const IST_OFFSET_MINUTES = 5 * 60 + 30;

const toIstDate = (value) => {
  if (!value) return null;
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(value);
  const parsed = new Date(hasTz ? value : `${value}Z`);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
};

// Whole days from today to the due date, in IST. 0 = today, negative = overdue.
export const daysUntilDue = (dueDate) => {
  const due = toIstDate(dueDate);
  if (!due) return null;
  const now = toIstDate(new Date().toISOString());
  const startOfDay = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((startOfDay(due) - startOfDay(now)) / 86400000);
};

export const formatDueDate = (dueDate) => {
  const due = toIstDate(dueDate);
  if (!due) return null;
  return due.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC', // already shifted to IST above
  });
};

// Wording and colour for how close the deadline is - kept in step with the
// reminder email so a reviewer sees the same status in both places.
// `className` styles the pill; `textClass` is for plain-text use, where a
// comfortable deadline stays neutral and only urgency earns colour.
export const dueTone = (days) => {
  if (days === null) return null;
  if (days < 0) {
    return {
      label: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`,
      short: 'Overdue',
      className: 'bg-red-50 text-red-700 border-red-200',
      textClass: 'text-red-600',
      icon: ExclamationTriangleIcon,
    };
  }
  if (days === 0) {
    return {
      label: 'Due today',
      short: 'Due today',
      className: 'bg-orange-50 text-orange-700 border-orange-200',
      textClass: 'text-orange-600',
      icon: ClockIcon,
    };
  }
  if (days <= 2) {
    return {
      label: days === 1 ? 'Due tomorrow' : `Due in ${days} days`,
      short: days === 1 ? 'Due tomorrow' : `${days} days left`,
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      textClass: 'text-amber-600',
      icon: ClockIcon,
    };
  }
  return {
    label: `Due in ${days} days`,
    short: `${days} days left`,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
    textClass: 'text-gray-500',
    icon: ClockIcon,
  };
};

const DueDateBadge = ({ dueDate, withDate = false, className = '' }) => {
  const days = daysUntilDue(dueDate);
  const tone = dueTone(days);
  if (!tone) return null;

  const Icon = tone.icon;
  return (
    <span
      className={`inline-flex items-center shrink-0 gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${tone.className} ${className}`}
      title={`Due ${formatDueDate(dueDate)}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {withDate ? `${tone.label} - ${formatDueDate(dueDate)}` : tone.label}
    </span>
  );
};

export default DueDateBadge;
